import { escapeHtml, markCurrentRow } from '@colplat/ui-web';
import { accountPath, accountShortLabel, buildAccountTree, type AccountNode } from './webledgerAccountTree.js';
import { inlineAccountIcon } from './webledgerAccountDisplay.js';
import type { AccountDto } from './webledgerOfflineState.js';
import { parseIdList, PREF, serializeIdList, type PrefsHandle } from './webledgerPrefs.js';

/**
 * The tree account picker — legacy's `acctpicker.js`.
 *
 * A `<select>` of 59 accounts is a flat list of names with no hierarchy and
 * no search, and in a book where `Cash` appears under three different parents
 * it is close to unusable: the reader has to know which of the three
 * identically-named options is theirs by position. So the control is the
 * tree, with a filter box over it, and a "Recent" section at the top because
 * in practice a few accounts account for most of the typing.
 *
 * One panel is built once and moved to whichever field is being edited, so a
 * transaction with eight splits does not build eight of these.
 *
 * Two pieces of state, both per-person and server-synced, both legacy's:
 * which folders you have closed, and which accounts you last chose. The
 * folder state is deliberately *not* written when the picker reveals the
 * current account's ancestors on opening — otherwise editing a deeply nested
 * account would slowly force every folder in the book open.
 */

const RECENT_MAX = 6;
/** Breathing room against the window edges when the panel is placed. */
const EDGE = 8;

export interface AccountPickerOptions {
  accounts: readonly AccountDto[];
  prefs: PrefsHandle;
  /** The account the field currently holds, revealed and marked on opening. */
  selectedId: string;
  onPick(id: string): void;
  /**
   * Whether a folder can be the answer, rather than only a way to reach one.
   *
   * Off for every field that books something, because nothing can be posted
   * to a placeholder. On for a filter, where a folder means "everything filed
   * under it" — and refusing it there made Assets, Expenses and Income the
   * accounts you could see and not choose. Then the name picks the folder
   * and the caret beside it opens and closes it.
   */
  foldersPickable?: boolean;
}

interface PickerState {
  panel: HTMLElement;
  input: HTMLInputElement;
  list: HTMLElement;
  trigger: HTMLElement | null;
  opts: AccountPickerOptions | null;
  rows: HTMLElement[];
  active: number;
}

let picker: PickerState | undefined;
let collapsed: Set<string> | undefined;
let recent: string[] | undefined;
/** Ancestors revealed for this opening only — never persisted. See the module comment. */
const forced = new Set<string>();

function loadState(prefs: PrefsHandle): void {
  if (!collapsed) collapsed = new Set(parseIdList(prefs.get(PREF.pickerCollapsed)));
  if (!recent) recent = parseIdList(prefs.get(PREF.recentAccounts));
}

function saveCollapsed(prefs: PrefsHandle): void {
  prefs.set(PREF.pickerCollapsed, serializeIdList(collapsed!));
}

function pushRecent(prefs: PrefsHandle, id: string): void {
  recent = [id, ...recent!.filter((x) => x !== id)].slice(0, RECENT_MAX);
  prefs.set(PREF.recentAccounts, serializeIdList(recent));
}

// ------------------------------------------------------------------ building

function build(): PickerState {
  if (picker) return picker;
  const panel = document.createElement('div');
  panel.className = 'wl-apk';
  panel.hidden = true;
  panel.innerHTML = `
    <div class="wl-apk__head">
      <input class="wl-apk__input" type="text" placeholder="Type to filter accounts" aria-label="Filter accounts">
      <button type="button" class="wl-apk__btn" data-apk-clear hidden aria-label="Clear the filter" title="Clear the filter">&times;</button>
      <button type="button" class="wl-apk__btn wl-apk__close" data-apk-close aria-label="Close without changing the account" title="Close without changing the account">&#10005;</button>
    </div>
    <div class="wl-apk__list" role="listbox" tabindex="-1"></div>
    <div class="wl-apk__foot"><span>&uarr;&darr; move</span><span>&crarr; choose</span><span>esc close</span></div>`;
  document.body.appendChild(panel);

  const state: PickerState = {
    panel,
    input: panel.querySelector<HTMLInputElement>('.wl-apk__input')!,
    list: panel.querySelector<HTMLElement>('.wl-apk__list')!,
    trigger: null, opts: null, rows: [], active: -1,
  };
  picker = state;

  state.input.addEventListener('input', () => { state.active = -1; render(); });
  state.input.addEventListener('keydown', onKey);
  state.list.addEventListener('keydown', onKey);
  panel.querySelector('[data-apk-clear]')!.addEventListener('click', () => {
    state.input.value = '';
    state.active = -1;
    render();
    state.input.focus();
  });
  panel.querySelector('[data-apk-close]')!.addEventListener('click', () => close());

  /**
   * The whole panel takes the wheel, not just the list.
   *
   * The panel is a search box, a scrolling tree and a row of hints, and only
   * the middle one is a scroll container — so a wheel over the search box did
   * nothing at all, and a wheel over the hints scrolled whatever was behind
   * the picker. That "behind" is the form it is floating over, which then
   * slid away underneath a panel that stays where it is (it is
   * `position:fixed`, anchored to the trigger).
   *
   * And the search box is exactly where the pointer already is: you click the
   * field, the panel opens under the cursor with its input on top, and the
   * first thing you try is the wheel. That is the report this fixes — "I open
   * the account selector and cannot scroll it".
   *
   * `preventDefault` rather than only forwarding, because a wheel over a
   * floating panel should never reach the page behind it. The list's own
   * `overscroll-behavior: contain` says the same thing for the case the list
   * does handle.
   */
  panel.addEventListener('wheel', (event) => {
    // Inside the list already: it scrolls itself, and `contain` stops the
    // chain at its ends. Re-applying the delta here would double the speed.
    if ((event.target as HTMLElement).closest('.wl-apk__list')) return;
    state.list.scrollTop += event.deltaY;
    event.preventDefault();
  }, { passive: false });

  // Capture phase: Bootstrap listens for Escape on the modal and would close
  // the whole transaction editor. The picker is on top, so it takes the key
  // first and stops it going any further.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !picker || picker.panel.hidden) return;
    event.preventDefault();
    event.stopPropagation();
    close();
  }, true);

  state.list.addEventListener('click', (event) => {
    const row = (event.target as HTMLElement).closest<HTMLElement>('.wl-apk__row');
    if (!row) return;
    const onCaret = (event.target as HTMLElement).closest('.wl-apk__caret') !== null;
    if (row.dataset['folder'] === '1' && (onCaret || !state.opts?.foldersPickable)) toggleFolder(row.dataset['id']!);
    else choose(row.dataset['id']!);
  });

  document.addEventListener('mousedown', (event) => {
    if (!picker || picker.panel.hidden) return;
    const target = event.target as Node;
    if (picker.panel.contains(target) || picker.trigger?.contains(target)) return;
    close();
  });
  window.addEventListener('resize', () => { if (picker && !picker.panel.hidden) place(); });
  return state;
}

// ------------------------------------------------------------- open and close

export function openAccountPicker(trigger: HTMLElement, opts: AccountPickerOptions): void {
  loadState(opts.prefs);
  const state = build();
  state.trigger = trigger;
  state.opts = opts;
  state.input.value = '';
  state.active = -1;

  /*
   * Into the dialog that owns the trigger, of either kind.
   *
   * A Bootstrap modal traps focus inside itself, so a panel left on `<body>`
   * could never be typed into. A native `<dialog>` opened with `showModal()`
   * is worse than that: it is in the browser's top layer, which paints over
   * everything outside it regardless of `z-index`, so the panel would not
   * only be untypable but invisible. Its descendants join it in the top
   * layer, so hosting the panel inside is what makes it appear at all.
   *
   * The panel is `position:fixed`, so it still lays out against the window
   * wherever it is mounted.
   */
  const host = trigger.closest('dialog, .modal') ?? document.body;
  if (state.panel.parentNode !== host) host.appendChild(state.panel);

  // Reveal the current account's ancestors, for this opening only.
  forced.clear();
  const byId = new Map(opts.accounts.map((a) => [a.id, a] as const));
  let parent = byId.get(opts.selectedId)?.parentId ?? null;
  for (let hops = 0; parent && hops < 64; hops += 1) {
    forced.add(parent);
    parent = byId.get(parent)?.parentId ?? null;
  }

  state.panel.hidden = false;
  render();
  place();
  state.input.focus();
  // After layout, so the row's position inside the scrolling list is final.
  requestAnimationFrame(() => revealSelected());
}

function close(): void {
  if (!picker) return;
  picker.panel.hidden = true;
  forced.clear();
  const trigger = picker.trigger;
  picker.trigger = null;
  // Focus goes back where it came from: the picker is a detour, and leaving
  // focus on a hidden panel strands the keyboard at the top of the document.
  trigger?.focus();
}

/**
 * Below the trigger when it fits, sliding up when it does not — floating over
 * the trigger if that is what it takes to show more of the tree.
 *
 * A tree wants height, so the panel is allowed the whole window rather than
 * only the gap under the button. A picker that shows four rows because the
 * field happens to sit near the bottom of the screen is a picker nobody uses.
 */
function place(): void {
  const state = picker!;
  const trigger = state.trigger;
  if (!trigger) return;
  const rect = trigger.getBoundingClientRect();
  const panel = state.panel;
  panel.style.width = `${Math.max(300, Math.min(420, rect.width))}px`;
  panel.style.maxHeight = `${window.innerHeight - EDGE * 2}px`;
  const height = panel.offsetHeight;
  let top = rect.bottom + 4;
  if (top + height > window.innerHeight - EDGE) top = window.innerHeight - EDGE - height;
  panel.style.top = `${Math.max(EDGE, top)}px`;
  panel.style.left = `${Math.max(EDGE, Math.min(rect.left, window.innerWidth - panel.offsetWidth - EDGE))}px`;
}

/**
 * Bring the account the field already holds into view and flash it, so the
 * panel opens by answering "where is the one I have now" before being asked.
 *
 * The same marker the chart of accounts and the register use — one
 * vocabulary for "this one", wherever it appears.
 */
function revealSelected(): void {
  const state = picker;
  if (!state || state.panel.hidden || !state.opts) return;
  const row = markCurrentRow(state.list, state.opts.selectedId, {
    attr: 'data-id', flash: true, scroll: 'always',
  });
  // Arrow keys continue from the current account rather than from the top.
  if (row) {
    state.active = state.rows.indexOf(row);
    paintActive();
  }
}

function toggleFolder(id: string): void {
  if (collapsed!.has(id)) collapsed!.delete(id);
  else { collapsed!.add(id); forced.delete(id); }
  saveCollapsed(picker!.opts!.prefs);
  render();
}

function choose(id: string): void {
  const opts = picker!.opts!;
  pushRecent(opts.prefs, id);
  close();
  opts.onPick(id);
}

// ---------------------------------------------------------------- rendering

function expanded(id: string): boolean {
  return !collapsed!.has(id) || forced.has(id);
}

/**
 * Every typed word must appear somewhere in the account's full path, in any
 * order — so `car ca` finds `Assets ➤ Car ➤ Cash`. Matching the path rather
 * than the name is what makes the filter usable in a book with three
 * accounts called "Cash".
 */
function matches(path: string, tokens: readonly string[]): boolean {
  const hay = path.toLowerCase();
  return tokens.every((token) => hay.includes(token));
}

interface WalkRow { node: AccountNode; depth: number; open: boolean }

function walk(
  node: AccountNode, depth: number, out: WalkRow[], tokens: readonly string[],
  pathOf: (id: string) => string,
): void {
  const childRows: WalkRow[] = [];
  for (const child of node.children) walk(child, depth + 1, childRows, tokens, pathOf);

  if (tokens.length > 0) {
    // Searching always reveals what matched: a hit three folders down is
    // useless if you still have to open three folders to reach it.
    if (!matches(pathOf(node.account.id), tokens) && childRows.length === 0) return;
    out.push({ node, depth, open: true });
    out.push(...childRows);
    return;
  }
  const open = expanded(node.account.id);
  out.push({ node, depth, open });
  if (open) out.push(...childRows);
}

function highlight(text: string, tokens: readonly string[]): string {
  let out = escapeHtml(text);
  // Longest first, so a short token cannot carve up a longer token's match
  // and leave the markup nested inside itself.
  for (const token of [...tokens].sort((a, b) => b.length - a.length)) {
    if (!token) continue;
    const re = new RegExp(`(${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
    out = out.replace(re, '<mark class="wl-apk__hit">$1</mark>');
  }
  return out;
}

function rowHtml(
  node: AccountNode, depth: number, open: boolean, opts: AccountPickerOptions,
  tokens: readonly string[], asPath: boolean,
): string {
  const account = node.account;
  const isFolder = account.isPlaceholder;
  const caret = node.children.length > 0
    ? `<span class="wl-apk__caret"><span class="cf-exp${open ? '' : ' is-closed'}" aria-hidden="true"></span></span>`
    : '<span class="wl-apk__caret"></span>';
  // A Recent row is out of the tree, so a bare name would be the one thing
  // the tree exists to disambiguate — but the whole chain is mostly "Assets"
  // repeated, and this panel is narrow enough to ellipsise it. Two segments,
  // the same rule the account fields use, with the full path on the row.
  const short = asPath ? accountShortLabel(opts.accounts, account.id) : undefined;
  const label = short ? short.label : account.name;
  const name = tokens.length > 0 ? highlight(label, tokens) : escapeHtml(label);
  const title = short ? ` title="${escapeHtml(short.full)}"` : '';
  const trailing = isFolder
    ? (node.children.length > 0 ? `<span class="wl-apk__count">${node.children.length}</span>` : '')
    : `<span class="wl-apk__cur">${escapeHtml(account.currencyCode)}</span>`;
  // Said on the row when a folder is pickable, because the two halves of it
  // now do different things and nothing else on screen says which is which.
  const titleAttr = isFolder && opts.foldersPickable && !short
    ? ` title="${escapeHtml(`${account.name} and everything under it — the arrow opens it`)}"` : title;
  return `<div class="wl-apk__row${isFolder ? ' wl-apk__row--folder' : ''}" data-id="${escapeHtml(account.id)}"${titleAttr} `
    + `data-folder="${isFolder ? 1 : 0}" role="option" aria-selected="${account.id === opts.selectedId}" `
    + `style="padding-left:${8 + depth * 14}px">`
    + `${caret}${inlineAccountIcon(opts.accounts, account.id)}`
    + `<span class="wl-apk__name">${name}</span>${trailing}</div>`;
}

function render(): void {
  const state = picker!;
  const opts = state.opts!;
  const clearBtn = state.panel.querySelector<HTMLElement>('[data-apk-clear]')!;
  clearBtn.hidden = state.input.value === '';

  const query = state.input.value.trim().toLowerCase();
  const tokens = query ? query.split(/\s+/) : [];
  const pathCache = new Map<string, string>();
  const pathOf = (id: string): string => {
    const hit = pathCache.get(id);
    if (hit !== undefined) return hit;
    const value = accountPath(opts.accounts, id);
    pathCache.set(id, value);
    return value;
  };

  const walked: WalkRow[] = [];
  // Same order as the chart of accounts, which is `sortOrder` then name.
  // Legacy sorts the picker's roots by account *type* instead; matching the
  // page the reader already knows beats matching legacy here, because the two
  // trees sitting side by side in different orders is its own small puzzle.
  for (const root of buildAccountTree(opts.accounts)) walk(root, 0, walked, tokens, pathOf);

  let html = '';
  if (tokens.length === 0) {
    const byId = new Map(opts.accounts.map((a) => [a.id, a] as const));
    // A folder chosen in a filter is remembered like any account, and shown
    // only where a folder is an answer: offered in a transaction form it
    // would be a Recent row that cannot be booked to.
    const shown = recent!.map((id) => byId.get(id))
      .filter((a): a is AccountDto => !!a && (opts.foldersPickable === true || !a.isPlaceholder));
    if (shown.length > 0) {
      // Full paths here, not bare names: a Recent list is out of the tree, so
      // the name alone would be the one thing the tree exists to disambiguate.
      html += '<div class="wl-apk__sec">Recent</div>'
        + shown.map((a) => rowHtml(
          { account: a, children: [], depth: 0, own: {}, subtree: {} }, 0, false, opts, [], true,
        )).join('')
        + '<div class="wl-apk__sec wl-apk__sec--top">All accounts</div>';
    }
  } else {
    const n = walked.filter((w) => !w.node.account.isPlaceholder
      && matches(pathOf(w.node.account.id), tokens)).length;
    html += `<div class="wl-apk__sec">${n} match${n === 1 ? '' : 'es'}</div>`;
  }
  html += walked.map((w) => rowHtml(w.node, w.depth, w.open, opts, tokens, false)).join('');
  if (walked.length === 0) html += '<div class="wl-apk__empty">No account matches that.</div>';

  state.list.innerHTML = html;
  state.rows = [...state.list.querySelectorAll<HTMLElement>('.wl-apk__row')];
  paintActive();
}

function paintActive(): void {
  const state = picker!;
  state.rows.forEach((row, i) => row.classList.toggle('wl-apk__row--active', i === state.active));
  state.rows[state.active]?.scrollIntoView({ block: 'nearest' });
}

function onKey(event: KeyboardEvent): void {
  const state = picker!;
  if (event.key === 'Escape') { event.preventDefault(); close(); return; }
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    if (state.rows.length === 0) return;
    const step = event.key === 'ArrowDown' ? 1 : -1;
    state.active = (state.active + step + state.rows.length) % state.rows.length;
    paintActive();
    return;
  }
  if (event.key === 'Enter') {
    event.preventDefault();
    // With no explicit choice, Enter takes the first *account* — the fast
    // path after typing two letters, and the reason folders are skipped.
    const row = state.rows[state.active] ?? state.rows.find((r) => r.dataset['folder'] === '0');
    if (!row) return;
    if (row.dataset['folder'] === '1' && !state.opts?.foldersPickable) toggleFolder(row.dataset['id']!);
    else choose(row.dataset['id']!);
    return;
  }
  /*
   * With Enter taken by "choose this folder", the keyboard needs another way
   * to open one: the arrows, as in any tree. Only while the search box is
   * empty, where they would otherwise move a caret through nothing, and only
   * on a folder row that has something to open.
   */
  if ((event.key === 'ArrowRight' || event.key === 'ArrowLeft') && state.opts?.foldersPickable
    && state.input.value === '') {
    const row = state.rows[state.active];
    if (!row || row.dataset['folder'] !== '1' || !row.querySelector('.wl-apk__caret')?.textContent) return;
    const isOpen = expanded(row.dataset['id']!);
    if ((event.key === 'ArrowRight') === isOpen) return;
    event.preventDefault();
    // Opening or closing only changes the rows AFTER this one, so the active
    // index still points at it. By id it would not: a folder in Recent is
    // the same id twice.
    const at = state.active;
    toggleFolder(row.dataset['id']!);
    state.active = at;
    paintActive();
  }
}
